import { PropsWithChildren, ReactNode, useRef } from "react";
import Spinner from "react-bootstrap/Spinner";
import { FormattedMessage } from "react-intl";
import {
  MainContextActionType,
  useMainContext,
} from "../../../context/MainContext";
import { SupportAlert } from "../../../hooks/useAlert";
import AlertLevel from "../../../models/alert";
import { Button, Modal } from "@shltelemedicine/shl-ui-lib";
import styles from "./AlertModal.module.css";

export type AlertModalProps = {
  show?: boolean;
  loading?: boolean;
  level?: AlertLevel;
  title?: ReactNode;
  support?: SupportAlert;
  dialog?: boolean;
  onClose?: () => void;
  okBtn?: {
    text?: ReactNode;
    onClick?: () => void;
    className?: string;
    bgColor?: string;
    icon?: {
      src: string;
      width?: number;
      height?: number;
    };
  };
  cancelBtn?: {
    text?: ReactNode;
    onClick?: () => void;
    className?: string;
    bgColor?: string;
    icon?: {
      src: string;
      width?: number;
      height?: number;
    };
  };
};

const AlertModal = ({
  show,
  level = AlertLevel.Error,
  title,
  loading,
  dialog,
  okBtn,
  cancelBtn,
  onClose,
  support,
  children,
}: PropsWithChildren<AlertModalProps>) => {
  const { dispatch } = useMainContext();

  const alertModalBody = useRef(null);

  const handleShowSupportModal = () => {
    dispatch({
      type: MainContextActionType.SET_SHOW_SUPPORT,
      payload: {
        show: true,
        subject: `${support?.subject} ${
          support?.code ? `- error code ${support.code}` : ""
        }`,
        description: support?.description,
      },
    });
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      dialogClassName={styles.dialog}
      contentClassName="h-100 app-shadow"
      onHide={onClose ?? cancelBtn?.onClick}
      footer={
        <div
          className={`${styles.footer} pt-3 px-3 d-flex justify-content-end align-items-end`}
        >
          {(dialog || cancelBtn) && (
            <Button
              className={cancelBtn?.className}
              icon={cancelBtn?.icon}
              onClick={cancelBtn?.onClick ?? onClose}
              bgColor={
                cancelBtn?.bgColor ??
                "transparent linear-gradient(180deg, #717689 0%, #393B45 100%) 0% 0% no-repeat padding-box"
              }
              disabled={loading}
            >
              <span className="capitalized">
                {cancelBtn?.text ?? (
                  <FormattedMessage
                    id="GLOBAL.CANCEL"
                    defaultMessage="Cancel"
                  />
                )}
              </span>
            </Button>
          )}
          <div className="mx-2"></div>
          <Button
            className={`capitalized ${okBtn?.className ?? ""}`}
            bgColor={
              okBtn?.bgColor ??
              "transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
            }
            icon={okBtn?.icon}
            disabled={loading}
            onClick={okBtn?.onClick ?? onClose}
          >
            <>
              {okBtn?.text ?? (
                <FormattedMessage id="GLOBAL.OK" defaultMessage="Ok" />
              )}
              {loading && (
                <>
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </>
          </Button>
        </div>
      }
    >
      <div className="w-75 mx-auto py-3 text-center">
        <h4 className="prim-color-dark">
          <b>
            {title ??
              (() => {
                switch (level) {
                  case AlertLevel.Info:
                    return (
                      <FormattedMessage
                        id="ALERT_MODAL.INFO.TITLE"
                        defaultMessage="Please Note"
                      />
                    );
                  case AlertLevel.Warning:
                    return (
                      <FormattedMessage
                        id="ALERT_MODAL.WARNING.TITLE"
                        defaultMessage="WARNING"
                      />
                    );
                  case AlertLevel.Error:
                  default:
                    return (
                      <FormattedMessage
                        id="ALERT_MODAL.ERROR.TITLE"
                        defaultMessage="Please Note"
                      />
                    );
                }
              })()}
          </b>
        </h4>
        <div ref={alertModalBody} className={styles.content}>
          {children}
        </div>
        {/* Link to support */}
        {support?.showLink && (
          <div className={`${styles.contactSupport} mt-4`}>
            <FormattedMessage
              id="ALERT_MODAL.CONTACT_SUPPORT_PREFIX"
              defaultMessage="Please"
            />
            &nbsp;
            <span role="button" onClick={handleShowSupportModal}>
              <u>
                <FormattedMessage
                  id="ALERT_MODAL.CONTACT_SUPPORT"
                  defaultMessage="contact SmartHeart customer support"
                />
              </u>
              .
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AlertModal;
